import { useFeedback } from "@taunggyi-plus/auth";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@taunggyi-plus/auth";

export type Receivable = {
  id: string;
  description: string;
  amount_minor: number;
  received_minor: number;
  category: string;
};
export function receiptStatus(entry: Receivable) {
  if (!["subscription_fee", "promotion_fee", "event_fee"].includes(entry.category))
    return "not_applicable";
  return Number(entry.received_minor) >= Number(entry.amount_minor)
    ? "received"
    : Number(entry.received_minor) > 0
      ? "partial"
      : "unrecorded";
}
export const receiptLabels: Record<string, string> = {
  received: "Received",
  partial: "Partially received",
  unrecorded: "Receipt not recorded",
  not_applicable: "Not applicable",
};
export function FeeReceiptDialog({
  entry,
  onClose,
  onSaved,
}: {
  entry: Receivable;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { client } = useAuth();
  const dialog = useRef<HTMLDialogElement>(null);
  const [requestId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const remaining = Number(entry.amount_minor) - Number(entry.received_minor);
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Yangon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client || busy) return;
    const data = new FormData(event.currentTarget);
    const amount = Math.round(Number(data.get("amount")) * 100);
    if (!Number.isSafeInteger(amount) || amount <= 0 || amount > remaining) {
      setMessage("Enter an amount within the remaining balance.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const result = await client.rpc("record_platform_fee_receipt", {
        target_entry: entry.id,
        target_amount_minor: amount,
        target_received_on: data.get("date"),
        target_reference: String(data.get("reference")).trim(),
        target_request_id: requestId,
      });
      if (result.error) throw new Error(result.error.message);
      onSaved();
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Receipt could not be saved. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="customer-order-filter-dialog"
      aria-labelledby="receipt-title"
      onCancel={(event) => {
        if (busy) event.preventDefault();
        else onClose();
      }}
    >
      <form onSubmit={submit}>
        <header>
          <h2 id="receipt-title">Record payment received</h2>
          <button
            className="button button--secondary"
            type="button"
            aria-label="Close receipt"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <p>{entry.description}</p>
        <p>Balance without a recorded receipt: {(remaining / 100).toLocaleString()} MMK</p>
        <label>
          Amount received (MMK)
          <input
            type="number"
            name="amount"
            min="0.01"
            max={remaining / 100}
            step="0.01"
            defaultValue={remaining / 100}
            required
          />
        </label>
        <label>
          Date received
          <input type="date" name="date" defaultValue={today} max={today} required />
        </label>
        <label>
          Receipt / payment reference
          <input name="reference" required maxLength={240} pattern=".*\S.*" />
        </label>
        <p>
          This records money already received. It does not collect a payment or create another
          income entry.
        </p>
        {message && <p role="alert">{message}</p>}
        <button className="button" disabled={busy}>
          {busy ? "Saving…" : "Record receipt"}
        </button>
      </form>
    </dialog>
  );
}
